/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import {
    useLocale,
    useRangeCalendar,
    type RangeCalendarProps as AriaRangeCalendarProps,
} from 'react-aria';
import { useRangeCalendarState } from 'react-stately';
import type { DateValue } from '@internationalized/date';

import { CalendarFrame, type CalendarFrameDisplayProps } from './CalendarFrame';
import { createCalendar } from './createCalendar';

export interface RangeCalendarProps<T extends DateValue>
    extends AriaRangeCalendarProps<T>, CalendarFrameDisplayProps {}

function RangeCalendarBase<T extends DateValue>({
    numVisibleMonths = 1,
    headingLevel = 2,
    pageBehavior = 'single',
    size = 'default',
    layout = 'paged',
    initialScrollDate,
    ...props
}: RangeCalendarProps<T>) {
    const ref = React.useRef<HTMLDivElement>(null);
    const { locale } = useLocale();
    const state = useRangeCalendarState({
        ...props,
        pageBehavior,
        visibleDuration: { months: numVisibleMonths },
        locale,
        createCalendar,
    });

    // The range hook needs the ref to finish a half-made selection when the
    // pointer is released outside the calendar.
    const aria = useRangeCalendar({ ...props, pageBehavior }, state, ref);

    return (
        <CalendarFrame
            state={state}
            aria={aria}
            calendarRef={ref}
            numVisibleMonths={numVisibleMonths}
            headingLevel={headingLevel}
            size={size}
            layout={layout}
            initialScrollDate={initialScrollDate}
        />
    );
}

/**
 * Memoized because the stacked layout renders hundreds of cells. Callers need
 * stable props (memoized `value`, stable callbacks) for this to skip renders.
 * The cast keeps the generic signature, which React.memo would otherwise drop.
 */
export const RangeCalendar = React.memo(RangeCalendarBase) as typeof RangeCalendarBase;
